import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Caret,
  Dropdown,
  Input,
  InputModal,
  Spinner,
  Subtitle,
  TagComponent,
  Title,
} from '../../components/shared';
import SeedingModal from '../../components/farm-modals/SeedingModal';
import AssessmentModal from '../../components/farm-modals/AssessmentModal';
import MaintenanceModal from '../../components/farm-modals/MaintenanceModal';
import FloatManageModal from '../../components/farm-modals/FloatManageModal';
import HarvestCompleteModal from '../../components/farm-modals/HarvestCompleteModal';
import WasteHarvestModal from '../../components/farm-modals/WasteHarvestModal';
import { translate } from '../../lib/lang.helper';
import { selectLang } from '../../store/ui/ui.selector';
import moment from 'moment';
import {
  areSimilarStrings,
  compareString,
  toCapitalCase,
  toMillisecond,
  toSecond,
} from '../../lib/common.helpers';
import { sendSingleRequest } from '../../apis';
import { ITaskResource } from '../../entities/task.entities';
import { selectFarmsData } from '../../store/farms/farms.selector';
import { Table, Button as OrgButton, Tabs } from 'antd';
import { roundNumber } from '../../entities/util-functions';
import { selectUtils } from '../../store/utils/utils.selector';
import { selectCustomTasks } from '../../store/tasks/tasks.selector';
import { getTaskAction, getTaskOptions } from '../../lib/task.helper';
import { showFeedback } from '../../store/ui/ui.actions';
import { toSecTimeTracks } from '../../components/shared/input/TimeTrackInput';
import { IFarmResource, ILineResource } from '../../entities/farms.entities';
import {
  updateAccountSetting,
  updateUserMeta,
} from '../../store/auth/auth.actions';
import { selectBoats } from '../../store/users/users.selector';
import SimpleTimeInput from '../../components/shared/input/SimpleTimeInput';
import { selectAccount, selectIsAdmin } from '../../store/auth/auth.selector';
import { selectTags } from '../../store/automation/automation.selector';
import LineTagModal from '../../components/automation/LineTagModal';

const timeStrToMinutes = (str: string) => {
  const [hours, minutes] = str.split(':');
  const totalMinutes = parseInt(hours) * 60 + parseInt(minutes);

  return isNaN(totalMinutes) ? 0 : totalMinutes;
};

const minutesToTimeStr = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const formattedHours = hours < 10 ? `0${hours}` : hours;
  const formattedMinutes = mins < 10 ? `0${mins}` : mins;
  return `${formattedHours}:${formattedMinutes}`;
};

const extractClientFromTitle = (title: string): string => {
  const match = title.match(/for (.+)$/);
  return match ? match[1] : title;
};

const formatTitle = (
  activity: string,
  farmName: string,
  lineName: string,
  clientName: string,
): string => {
  return `${activity} ${farmName} ${lineName} for ${clientName}`;
};

const InputPopup: FC<{
  text: string;
  onConfirm: (d: string) => void;
  onCancel: () => void;
}> = ({ text, onCancel, onConfirm }) => {
  const lang = useSelector(selectLang);
  const commentTemplates = useSelector(selectUtils)
    .filter(x => x.type === 'comment_template')
    .map(x => x.name);

  const [value, setValue] = useState(text);

  const toLabel = (s: string) => (s.length > 55 ? `${s.slice(0, 55)}...` : s);
  const confirmClick = () => onConfirm(value);

  return (
    <InputModal
      visible={true}
      type='confirm'
      title={translate(lang, 'Update comment')}
      onCancel={onCancel}
      onConfirm={confirmClick}
    >
      {commentTemplates.length > 0 && (
        <div className='mb-18'>
          <Dropdown
            label={translate(lang, 'Select a sentence')}
            options={commentTemplates.map((template, id) => ({
              id: id.toString(),
              label: toLabel(template),
              value: template,
            }))}
            onChange={value => setValue(value)}
          />
        </div>
      )}
      <div className='mb-18'>
        <Input
          type='textarea'
          value={value}
          onChange={e => setValue(e.target.value)}
          className='w-100'
        />
      </div>
    </InputModal>
  );
};

const getMonday = () => {
  const monday = new Date();
  const day = monday.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  monday.setDate(monday.getDate() + diff);
  return monday.toLocaleDateString('en-CA');
};

const getWeekOptions = (mondayStr: string) => {
  let result: { label: string; value: string }[] = [];
  for (let i = 0; i < 7; i++) {
    const date = moment(mondayStr).add(i, 'days');
    result.push({
      value: date.format('YYYY-MM-DD'),
      label: date.format('dddd (MMM Do)'),
    });
  }
  return result;
};

interface IRow {
  id: number;
  farm_id: number | undefined;
  line_id: number | undefined;
  client: string;
  boat_id: number | null;
  activity: ITaskResource['action'];
  activityLabel: string;
  originalType?: ITaskResource['action'];
  comment: string | null;
  start_time: string;
  finish_time: string;
  baseTime: string;
  form_data?: any;
  has_completed_object?: boolean;
  is_changed: boolean;
  is_valid: boolean;
}

const defaultRow = (
  lastRow: IRow | undefined,
  baseTime: string | undefined = undefined,
  refer: IFilter | undefined = undefined,
): IRow => ({
  id: -Date.now(),
  start_time: lastRow?.finish_time ?? '',
  finish_time: lastRow
    ? minutesToTimeStr(timeStrToMinutes(lastRow.finish_time) + 60)
    : '',
  baseTime: baseTime ?? '',
  farm_id: refer?.farm_id,
  line_id: refer?.line_id,
  client: '',
  boat_id: refer?.boat_id ?? null,
  activity: null,
  activityLabel: 'General',
  comment: null,
  form_data: null,
  is_changed: false,
  is_valid: true,
});

const getChangedFormData = (row: IRow, key?: keyof IRow) => {
  if (row.id >= 0) {
    return row.form_data;
  }
  const res: any = {
    farm_id: row.farm_id,
    line_id: row.line_id,
    time_tracks: toSecTimeTracks([
      {
        start_time: toTimestamp(row.baseTime, row.start_time),
        finish_time: toTimestamp(row.baseTime, row.finish_time),
      },
    ]),
    boat_id: row.boat_id,
    comment: row.comment,
  };
  let patch: any = null;
  if (row.activity === 'SEEDING') {
    patch = { ...res, planned_date_seed: toTimestamp(row.baseTime, '') };
  } else if (row.activity === 'ASSESSMENT') {
    patch = { ...res, assessment_date: toTimestamp(row.baseTime, '') };
  } else if (row.activity === 'MAINTENANCE') {
    patch = { ...res, maintain_date: toTimestamp(row.baseTime, '') };
  } else if (row.activity === 'INVENTORY_SEED') {
    patch = { ...res, manage_date: toTimestamp(row.baseTime, '') };
  } else if (row.activity === 'HARVEST') {
    patch = { ...res, complete_date: toTimestamp(row.baseTime, '') };
  } else {
    return row.form_data;
  }
  if (patch && row.form_data && key !== 'activity') {
    const { farm_id, line_id, time_tracks, boat_id, comment } = patch;
    return {
      ...row.form_data,
      farm_id,
      line_id,
      time_tracks,
      boat_id,
      comment,
    };
  }
  return patch;
};

const toTimestamp = (date: string, time: string) =>
  moment(`${date} ${time}`).toDate().getTime();

const makeQueueData = async (
  farms: IFarmResource[],
  type: ITaskResource['action'],
  task: ITaskResource,
  showError: (s: string) => void,
  lang: any,
) => {
  const farm = farms.find(f => f.id === task.farm_id);
  const line = farm?.lines.find(l => l.id === task.line_id);

  if (!line) {
    showError(translate(lang, `Task ${task.title} is not assigned to a line`));
    return false;
  }

  if (type === 'SEEDING') {
    const data = {
      ...task.form_data,
      line_id: task.line_id,
      task_id: task.id,
      boat_id: task.boat?.id,
      time_tracks: task.time_tracks,
    };
    const response = await sendSingleRequest(
      { type: 'seeding', data, task_id: task.id },
      'POST',
      'api/queue/entity',
      true,
    );
    if (!response.status) {
      showError(translate(lang, response.data?.message ?? 'Unknown error'));
    }
    return response.status;
  } else if (type === 'ASSESSMENT') {
    if (!line.growing_cycle) {
      showError(translate(lang, '_line_empty'));
      return false;
    }
    const data = {
      seeding_id: line.growing_cycle.main_seed.id,
      ...task.form_data,
      line_id: task.line_id,
      task_id: task.id,
      boat_id: task.boat?.id,
      time_tracks: task.time_tracks,
    };
    const response = await sendSingleRequest(
      { type: 'assessment', data, task_id: task.id },
      'POST',
      'api/queue/entity',
      true,
    );
    if (!response.status) {
      showError(translate(lang, response.data?.message ?? 'Unknown error'));
    }
    return response.status;
  } else if (type === 'MAINTENANCE') {
    const data = {
      seeding_id: line.growing_cycle?.main_seed.id ?? undefined,
      ...task.form_data,
      line_id: task.line_id,
      task_id: task.id,
      boat_id: task.boat?.id,
      time_tracks: task.time_tracks,
    };
    const response = await sendSingleRequest(
      { type: 'maintenance', data, task_id: task.id },
      'POST',
      'api/queue/entity',
      true,
    );
    if (!response.status) {
      showError(translate(lang, response.data?.message ?? 'Unknown error'));
    }
    return response.status;
  } else if (type === 'INVENTORY_SEED') {
    if (!line.growing_cycle) {
      showError(translate(lang, '_line_empty'));
      return false;
    }
    const data = {
      seeding_id: line.growing_cycle.main_seed.id,
      ...task.form_data,
      line_id: line.id,
      task_id: task.id,
      boat_id: task.boat?.id,
      time_tracks: task.time_tracks,
    };
    const response = await sendSingleRequest(
      { type: 'inventory_seed', data, task_id: task.id },
      'POST',
      'api/queue/entity',
      true,
    );
    if (!response.status) {
      showError(translate(lang, response.data?.message ?? 'Unknown error'));
    }
    return response.status;
  } else if (type === 'HARVEST') {
    if (!line.growing_cycle) {
      showError(translate(lang, '_line_empty'));
      return false;
    }
    const data = {
      seeding_id: line.growing_cycle.main_seed.id,
      is_waste: task.type?.toLowerCase().includes('waste') ? true : undefined,
      ...task.form_data,
      line_id: line.id,
      task_id: task.id,
      boat_id: task.boat?.id,
      time_tracks: task.time_tracks,
    };
    const response = await sendSingleRequest(
      { type: 'harvest', data, task_id: task.id },
      'POST',
      'api/queue/entity',
      true,
    );
    if (!response.status) {
      showError(translate(lang, response.data?.message ?? 'Unknown error'));
    }
    return response.status;
  } else if (type === 'TAGGING') {
    const data = {
      farm_id: line.farm_id,
      line_id: line.id,
      task_id: task.id,
      boat_id: task.boat?.id,
      time_tracks: task.time_tracks,
    };
    const response = await sendSingleRequest(
      { type: 'tagging', data, task_id: task.id },
      'POST',
      'api/queue/entity',
      true,
    );
    if (!response.status) {
      showError(translate(lang, response.data?.message ?? 'Unknown error'));
    }
    return response.status;
  } else {
    return false;
  }
};

const actionsLineMustFilled = [
  'SEEDING',
  'ASSESSMENT',
  'INVENTORY_SEED',
  'HARVEST',
];

interface IFilter {
  farm_id?: number;
  line_id?: number;
  boat_id?: number;
}

const TaskTracksPage = () => {
  const lang = useSelector(selectLang);
  const dispatch = useDispatch<any>();

  const farmsData = useSelector(selectFarmsData).sort((a, b) =>
    compareString(a.farm_number, b.farm_number),
  );
  const customers = useSelector(selectUtils).filter(x => x.type === 'customer');
  const customTasks = useSelector(selectCustomTasks);
  const boats = useSelector(selectBoats);
  const accountSetting = useSelector(selectAccount);
  const isAdmin = useSelector(selectIsAdmin);
  const tags = useSelector(selectTags);

  const [loading, setLoading] = useState(false);
  const [mondayDate, setMondayDate] = useState(getMonday());
  const [curDate, setCurDate] = useState(mondayDate);
  const [weekData, setWeekData] = useState<IRow[]>([]);
  const [openedRow, setOpenedRow] = useState<IRow>();
  const [filterOptions, setFilterOptions] = useState<IFilter>({});
  const [timeFormat, setTimeFormat] = useState(
    accountSetting?.display_time_format ?? '12H',
  );
  const [taggingLine, setTaggingLine] = useState<ILineResource>();
  const [openedFormRow, setOpenedFormRow] = useState<IRow>();

  const loadData = useCallback(async () => {
    const date_from = toSecond(moment(mondayDate).toDate().getTime());
    const date_to = toSecond(
      moment(mondayDate).add(7, 'days').toDate().getTime(),
    );
    setLoading(true);
    const response = await sendSingleRequest(
      { date_from, date_to },
      'GET',
      'api/task/tasks',
      true,
    );
    setLoading(false);
    if (!response.status) {
      showError(translate(lang, response.data?.message ?? 'Unknown error'));
      return;
    }
    let tasks = response.data.data as ITaskResource[];
    tasks = tasks.filter(x => x.time_tracks && x.time_tracks.length > 0);
    tasks.sort((a, b) => {
      if (!a.time_tracks) return -1;
      if (!b.time_tracks) return 1;
      return a.time_tracks[0].start_time - b.time_tracks[0].start_time;
    });
    const data: IRow[] = tasks.map((x: ITaskResource) => {
      const farm = farmsData.find(f => f.id === x.farm_id);
      const line = farm?.lines.find(l => l.id === x.line_id);
      const tracks = x.time_tracks ?? [];

      return {
        id: x.id,
        farm_id: farm?.id,
        line_id: line?.id,
        client: extractClientFromTitle(x.title),
        boat_id: x.boat?.id ?? null,
        activity: x.action,
        activityLabel:
          x.type === 'INVENTORY_SEED'
            ? 'Inventory Manage'
            : toCapitalCase(x.type ?? 'Generic task'),
        originalType: x.action,
        comment: x.content,
        baseTime: moment(toMillisecond(tracks[0].start_time)).format(
          'YYYY-MM-DD',
        ),
        start_time: moment(toMillisecond(tracks[0].start_time)).format('HH:mm'),
        finish_time: moment(
          toMillisecond(tracks[tracks.length - 1].finish_time),
        ).format('HH:mm'),
        form_data: undefined,
        has_completed_object: x.has_completed_object,
        is_changed: false,
        is_valid: true,
      };
    });
    setWeekData(data);
  }, [mondayDate]);

  const filtered = useMemo(
    () =>
      weekData
        .filter(x => x.baseTime === curDate)
        .filter(x => {
          if (filterOptions.farm_id && x.farm_id !== filterOptions.farm_id) {
            return false;
          }
          if (filterOptions.line_id && x.line_id !== filterOptions.line_id) {
            return false;
          }
          if (filterOptions.boat_id && x.boat_id !== filterOptions.boat_id) {
            return false;
          }
          return true;
        }),
    [weekData, curDate, filterOptions],
  );

  const showError = (message: string) =>
    dispatch(
      showFeedback({
        isMessage: true,
        message,
        type: 'error',
        duration: 10000,
      }),
    );

  const updateTimeFormat = (format: string) => {
    dispatch(updateAccountSetting({ display_time_format: format }));
    setTimeFormat(format as '24H' | '12H');
  };
  const clickPrev = () => {
    const prevDate = moment(mondayDate)
      .subtract(7, 'days')
      .toDate()
      .toLocaleDateString('en-CA');
    setMondayDate(prevDate);
    setCurDate(prevDate);
  };
  const clickNext = () => {
    const nextDate = moment(mondayDate)
      .add(7, 'days')
      .toDate()
      .toLocaleDateString('en-CA');
    setMondayDate(nextDate);
    setCurDate(nextDate);
  };
  const updateForm = (id: number, key: keyof IRow, value: any) => {
    let item = filtered.find(x => x.id === id) as IRow;
    if (key === 'farm_id') {
      const farm = farmsData.find(x => x.id === value);
      const customer =
        farm?.owners && farm.owners.length > 0
          ? customers.find(c => areSimilarStrings(c.name, farm.owners[0].title))
              ?.name
          : undefined;
      item = {
        ...item,
        farm_id: farm?.id,
        line_id: undefined,
        client: customer ?? '',
      };
    } else if (key === 'line_id') {
      const farm = farmsData.find(x => x.id === item.farm_id);
      const line = farm?.lines.find(x => x.id === value);
      item = { ...item, line_id: line?.id, form_data: null };
    } else if (key === 'activity') {
      item = {
        ...item,
        activity: getTaskAction(customTasks, value),
        activityLabel: value,
      };
    } else {
      item = { ...item, [key]: value };
    }
    item.is_changed = true;
    item.is_valid = true;
    if (key !== 'form_data' && item.id < 0) {
      item.form_data = getChangedFormData(item, key);
    }
    setWeekData(weekData.map(x => (x.id === id ? item : x)));
  };
  const deleteRow = async (d: IRow) => {
    if (d.id > 0) {
      setLoading(true);
      const response = await sendSingleRequest(
        {},
        'DELETE',
        `api/task/tasks/${d.id}`,
        true,
      );
      setLoading(false);
      if (!response.status) {
        showError(translate(lang, response.data?.message ?? 'Unknown error'));
        return;
      }
      setWeekData(weekData.filter(x => x.id !== d.id));
    } else {
      setWeekData(weekData.filter(x => x.id !== d.id));
    }
  };
  const openFormRow = async (x: IRow) => {
    if (!x.farm_id || !x.line_id) {
      showError(translate(lang, '_sel_farm_line'));
      return;
    }
    if (x.id > 0 && x.form_data === undefined) {
      setLoading(true);
      const response = await sendSingleRequest(
        {},
        'GET',
        `api/task/tasks/${x.id}`,
        true,
      );
      setLoading(false);
      if (!response.status) {
        showError(translate(lang, response.data?.message ?? 'Unknown error'));
        return;
      }
      const task = response.data as ITaskResource;
      const updatedRow: IRow = {
        ...x,
        form_data: task.form_data ?? null,
        has_completed_object: task.has_completed_object,
      };
      setWeekData(weekData.map(r => (r.id === x.id ? updatedRow : r)));
      setOpenedFormRow(updatedRow);
    } else {
      setOpenedFormRow(x);
    }
  };
  const addNewRow = () => {
    const last =
      filtered.length > 0 ? filtered[filtered.length - 1] : undefined;
    let tmp = defaultRow(last, curDate, filterOptions);
    setWeekData([...weekData, tmp]);
  };
  const saveClick = async () => {
    const dataRows = filtered.filter(x => x.id < 0 || x.is_changed);
    const invalidIDs: number[] = [];
    for (const row of dataRows) {
      if (!row.client) {
        showError(translate(lang, 'Client is required'));
        invalidIDs.push(row.id);
        continue;
      }
      const start_time = toTimestamp(row.baseTime, row.start_time);
      let finish_time = toTimestamp(row.baseTime, row.finish_time);
      if (finish_time < start_time) {
        finish_time += 24 * 3600 * 1000;
      }
      const time_tracks = toSecTimeTracks([{ start_time, finish_time }]);
      if (!time_tracks) {
        showError(translate(lang, 'Invalid time tracks'));
        invalidIDs.push(row.id);
        continue;
      }
      const t = customTasks?.find(x => x.name === row.activity);
      if (
        (t && t.action && actionsLineMustFilled.includes(t.action)) ||
        (row.activity && actionsLineMustFilled.includes(row.activity))
      ) {
        const farm = farmsData.find(f => f.id === row.farm_id);
        const line = farm?.lines.find(l => l.id === row.line_id);
        if (!line) {
          showError(translate(lang, `Line is required for ${row.activity}`));
          invalidIDs.push(row.id);
          continue;
        }
        if (
          ((t && t.action && t.action !== 'SEEDING') ||
            row.activity !== 'SEEDING') &&
          !line.growing_cycle
        ) {
          showError(
            translate(lang, `The line for ${row.activity} is empty now`),
          );
          invalidIDs.push(row.id);
          continue;
        }
      }
    }
    if (invalidIDs.length > 0) {
      setWeekData(
        weekData.map(x => ({ ...x, is_valid: !invalidIDs.includes(x.id) })),
      );
      return;
    }
    setLoading(true);
    let needToLoadQueue = false;
    for (const row of dataRows) {
      const start_time = toTimestamp(row.baseTime, row.start_time);
      let finish_time = toTimestamp(row.baseTime, row.finish_time);
      if (finish_time < start_time) {
        finish_time += 24 * 3600 * 1000;
      }
      const farm = farmsData.find(f => f.id === row.farm_id);
      const line = farm?.lines.find(l => l.id === row.line_id);
      const farmName = farm ? `${farm.name}` : '';
      const lineName = line ? line.line_name : '';
      const formattedTitle = formatTitle(
        row.activityLabel,
        farmName,
        lineName,
        row.client,
      );
      const baseData = {
        farm_id: row.farm_id,
        line_id: row.line_id,
        title: formattedTitle,
        content: row.comment,
        action: row.activity,
        type: row.activityLabel,
        time_tracks: toSecTimeTracks([{ start_time, finish_time }]),
        boat_id: row.boat_id,
        form_data: row.form_data,
      };
      if (row.id <= 0) {
        const data = { ...baseData, due_date: start_time, is_completed: true };
        const response = await sendSingleRequest(
          data,
          'POST',
          'api/task/tasks',
          true,
        );
        if (!response.status) {
          showError(translate(lang, response.data?.message ?? 'Unknown error'));
          continue;
        }
        const task = {
          ...(response.data as ITaskResource),
          form_data: row.form_data,
        };
        if (await makeQueueData(farmsData, row.activity, task, showError, lang))
          needToLoadQueue = true;
      } else {
        const response = await sendSingleRequest(
          baseData,
          'PUT',
          `api/task/tasks/${row.id}`,
          true,
        );
        if (!response.status) {
          showError(translate(lang, response.data?.message ?? 'Unknown error'));
        }
      }
    }
    if (needToLoadQueue) {
      dispatch(updateUserMeta());
    }
    await loadData();
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (filtered.length === 0) {
      setWeekData([...weekData, defaultRow(undefined, curDate, filterOptions)]);
    }
  }, [filtered, curDate]);

  const totalSumMinutes = filtered.reduce((acc, x) => {
    let minutes =
      timeStrToMinutes(x.finish_time) - timeStrToMinutes(x.start_time);
    if (minutes < 0) {
      minutes += 24 * 60;
    }
    return acc + minutes;
  }, 0);

  const columns = [
    {
      title: translate(lang, 'Start Time'),
      key: 'start_time',
      render: (x: IRow) => (
        <SimpleTimeInput
          value={x.start_time}
          onChange={v => updateForm(x.id, 'start_time', v)}
          showAmPm={timeFormat === '12H'}
        />
      ),
      width: 120,
    },
    {
      title: translate(lang, 'Finish Time'),
      key: 'finish_time',
      render: (x: IRow) => (
        <SimpleTimeInput
          value={x.finish_time}
          onChange={v => updateForm(x.id, 'finish_time', v)}
          showAmPm={timeFormat === '12H'}
        />
      ),
      width: 120,
    },
    {
      title: translate(lang, 'Farm'),
      key: 'farm_id',
      render: (x: IRow) => (
        <Dropdown
          placeholder={translate(lang, 'Select Farm')}
          value={x.farm_id?.toString()}
          options={farmsData.map(f => ({
            id: f.id.toString(),
            label: `${f.name} (${f.farm_number})`,
            value: f.id.toString(),
          }))}
          onChange={v => updateForm(x.id, 'farm_id', Number(v))}
          showSearch={true}
          disabled={!!x.has_completed_object}
        />
      ),
      width: 225,
    },
    {
      title: translate(lang, 'Line'),
      key: 'farm_line',
      render: (x: IRow) => (
        <Dropdown
          placeholder={translate(lang, 'Select Line')}
          value={x.line_id?.toString()}
          options={
            farmsData
              .find(f => f.id === x.farm_id)
              ?.lines.map(l => {
                let label: any = l.line_name;
                if (l.line_group) {
                  label = `${label} (${l.line_group.name})`;
                }
                return {
                  id: l.id.toString(),
                  label,
                  value: l.id.toString(),
                };
              }) ?? []
          }
          onChange={v => updateForm(x.id, 'line_id', Number(v))}
          showSearch={true}
          disabled={!!x.has_completed_object}
        />
      ),
      width: 50,
    },
    {
      title: translate(lang, 'Tags'),
      key: 'tags',
      render: (x: IRow) => {
        const sourceLine = farmsData
          .find(f => f.id === x.farm_id)
          ?.lines.find(l => l.id === x.line_id);
        if (!sourceLine) {
          return <></>;
        }
        const tagged = tags?.filter(t =>
          t.tag_lines.some(tl => tl.line_id === x.line_id),
        );
        if (!tagged || tagged.length === 0) {
          return (
            <div className='d-flex justify-center'>
              <OrgButton
                size='small'
                type='primary'
                danger={true}
                disabled={loading}
                onClick={() => setTaggingLine(sourceLine)}
              >
                {translate(lang, 'No tagged')}
              </OrgButton>
            </div>
          );
        }
        return (
          <div
            className='d-flex flex-col gap-7'
            onClick={() => setTaggingLine(sourceLine)}
          >
            {tagged.map(t => (
              <TagComponent
                key={t.id}
                className='cursor-pointer'
                color={t.is_urgent ? 'red' : 'green'}
              >
                {t.name}
              </TagComponent>
            ))}
          </div>
        );
      },
    },
    {
      title: translate(lang, 'Client'),
      key: 'client',
      render: (x: IRow) =>
        customers.length > 0 ? (
          <div className='mxw-270'>
            <Dropdown
              options={customers.map(c => ({
                id: c.name,
                label: c.name,
                value: c.name,
              }))}
              value={x.client}
              onChange={v => updateForm(x.id, 'client', v)}
              showSearch={true}
            />
          </div>
        ) : (
          <div className='mxw-270'>
            <Input
              type='text'
              value={x.client}
              onChange={e => updateForm(x.id, 'client', e.target.value)}
              className='w-100'
            />
          </div>
        ),
      width: 250,
    },
    {
      title: translate(lang, 'Vessel'),
      key: 'boat_id',
      render: (x: IRow) => (
        <Dropdown
          options={boats.map(b => ({
            id: b.id.toString(),
            value: b.id.toString(),
            label: `${b.name} (${b.reg_number})`,
          }))}
          value={x.boat_id?.toString()}
          onChange={v => updateForm(x.id, 'boat_id', Number(v))}
        />
      ),
      width: 200,
    },
    {
      title: translate(lang, 'Activity'),
      key: 'activity',
      render: (x: IRow) => (
        <div className='mxw-200'>
          <Dropdown
            options={getTaskOptions(lang, customTasks)}
            value={x.activityLabel}
            onChange={v => updateForm(x.id, 'activity', v)}
            disabled={!!x.has_completed_object}
          />
        </div>
      ),
      width: 200,
    },
    {
      title: translate(lang, 'Comment'),
      key: 'comment',
      render: (x: IRow) => (
        <div
          className='btn__modal ml-16'
          onKeyDown={() => undefined}
          onClick={() => setOpenedRow(x)}
        >
          {translate(lang, 'View')}
        </div>
      ),
    },
    {
      title: translate(lang, 'Form'),
      key: 'form',
      render: (x: IRow) =>
        [
          'SEEDING',
          'ASSESSMENT',
          'MAINTENANCE',
          'INVENTORY_SEED',
          'HARVEST',
        ].includes(x.activity ?? '') ? (
          <div
            className='btn__modal ml-16'
            onKeyDown={() => undefined}
            onClick={() => openFormRow(x)}
          >
            {translate(lang, 'View')}
          </div>
        ) : (
          <></>
        ),
    },
    {
      title: translate(lang, 'Total Hours'),
      key: 'total_hours',
      render: (x: IRow) => {
        let totalMinutes =
          timeStrToMinutes(x.finish_time) - timeStrToMinutes(x.start_time);
        if (totalMinutes < 0) {
          totalMinutes += 24 * 60;
        }
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return !minutes ? (
          <div className='text-center'>{`${hours} h`}</div>
        ) : !hours ? (
          <div className='text-center'>{`${minutes} m`}</div>
        ) : (
          <div className='text-center'>{`${hours}h ${minutes}m`}</div>
        );
      },
      width: 100,
    },
    {
      title: '',
      key: 'action',
      render: (x: IRow) => (
        <OrgButton
          size='small'
          type='primary'
          danger={true}
          onClick={() => deleteRow(x)}
          disabled={loading || !isAdmin}
        >
          {translate(lang, 'Delete')}
        </OrgButton>
      ),
    },
  ];

  return (
    <div className='bg-secondary min-h-80'>
      <div className='container pb-32'>
        <div className='mb-32 pt-24'>
          <Title size={5} color='black' align='default' fontWeight={700}>
            {translate(lang, 'TimeSheet')}
          </Title>
        </div>
        <div className='mb-32 d-flex justify-between align-end'>
          <div className='d-flex justify-between align-center white-card-small pt-3 pb-3'>
            <Button
              color='blue'
              size={3}
              width='default'
              type='transparent'
              className='mr-26'
              name='prev'
              onClick={clickPrev}
              onlyIconDisabled
            >
              <Caret color='#5A607F' direction='left' />
            </Button>
            <Subtitle size={4} color='black' align='left' fontWeight={500}>
              {`${moment(mondayDate).format('DD/MM/YYYY')} - ${moment(
                mondayDate,
              )
                .add(6, 'days')
                .format('DD/MM/YYYY')}`}
            </Subtitle>
            <Button
              color='blue'
              size={3}
              width='default'
              type='transparent'
              className='ml-26'
              name='next'
              onClick={clickNext}
              onlyIconDisabled
            >
              <Caret color='#5A607F' direction='right' />
            </Button>
          </div>
          <div className='d-flex'>
            <Button
              type='fill'
              color='green'
              size={3}
              width='middle'
              onClick={saveClick}
              disabled={loading}
              className='mr-16'
            >
              {translate(lang, 'Save')}
            </Button>
            <Button
              type='fill'
              color='blue'
              size={3}
              width='middle'
              onClick={addNewRow}
              disabled={loading}
              className='ml-16'
            >
              {translate(lang, 'Add new line')}
            </Button>
          </div>
        </div>
        {loading && (
          <div className='loader'>
            <Spinner />
          </div>
        )}
        <div className='mt-12 mb-17 d-flex justify-center'>
          <div className='filter-option mr-12'>
            <Dropdown
              label={translate(lang, 'Farm')}
              placeholder={translate(lang, 'Select Farm')}
              value={filterOptions.farm_id?.toString() ?? '-'}
              options={[
                {
                  id: '-',
                  label: translate(lang, 'All Farms'),
                  value: '-',
                },
                ...farmsData.map(f => ({
                  id: f.id.toString(),
                  label: `${f.name} (${f.farm_number})`,
                  value: f.id.toString(),
                })),
              ]}
              onChange={v =>
                setFilterOptions({
                  ...filterOptions,
                  farm_id: v === '-' ? undefined : Number(v),
                })
              }
            />
          </div>
          <div className='filter-option mr-12'>
            <Dropdown
              label={translate(lang, 'Vessel')}
              placeholder={translate(lang, 'Select Vessel')}
              value={filterOptions.boat_id?.toString() ?? '-'}
              options={[
                {
                  id: '-',
                  value: '-',
                  label: translate(lang, 'All Vessels'),
                },
                ...boats.map(b => ({
                  id: b.id.toString(),
                  value: b.id.toString(),
                  label: `${b.name} (${b.reg_number})`,
                })),
              ]}
              onChange={v =>
                setFilterOptions({
                  ...filterOptions,
                  boat_id: v === '-' ? undefined : Number(v),
                })
              }
            />
          </div>
          <div className='filter-option'>
            <Dropdown
              label={translate(lang, 'Time Format')}
              placeholder={translate(lang, 'Select Time Format')}
              value={timeFormat ?? '24H'}
              options={[
                {
                  id: '12H',
                  value: '12H',
                  label: translate(lang, '12 Hour Format'),
                },
                {
                  id: '24H',
                  value: '24H',
                  label: translate(lang, '24 Hour Format'),
                },
              ]}
              onChange={v => updateTimeFormat(v)}
            />
          </div>
        </div>
        <Tabs type='line' activeKey={curDate} onChange={t => setCurDate(t)}>
          {getWeekOptions(mondayDate).map(x => (
            <Tabs.TabPane key={x.value} tab={x.label}>
              {x.value === curDate && (
                <div className='pb-24 w-100'>
                  <Table
                    rowKey={'id'}
                    className='table table--units table--form'
                    columns={columns}
                    dataSource={filtered}
                    pagination={false}
                    rowClassName={row => (row.is_valid ? '' : 'invalid-row')}
                  />
                  <div className='text-right mt-6 mr-24'>
                    <strong>
                      {`Total: ${roundNumber(totalSumMinutes / 60)} hours`}
                    </strong>
                  </div>
                </div>
              )}
            </Tabs.TabPane>
          ))}
        </Tabs>
      </div>
      {openedRow && (
        <InputPopup
          text={openedRow.comment ?? ''}
          onCancel={() => setOpenedRow(undefined)}
          onConfirm={v => {
            updateForm(openedRow.id, 'comment', v);
            setOpenedRow(undefined);
          }}
        />
      )}
      {openedFormRow &&
        (() => {
          const farm = farmsData.find(f => f.id === openedFormRow.farm_id);
          const line = farm?.lines.find(l => l.id === openedFormRow.line_id);
          if (!line) return null;

          const handleCancel = () => setOpenedFormRow(undefined);
          const storeAndClose = (d: any) => {
            setWeekData(prev =>
              prev.map(x =>
                x.id === openedFormRow.id
                  ? { ...x, form_data: d, is_changed: true }
                  : x,
              ),
            );
            setOpenedFormRow(undefined);
          };
          const overConfirmDefault = async (d: any) => {
            storeAndClose(d);
            return true;
          };
          const overConfirmHarvest = async (d: any) => {
            storeAndClose(d);
            return { status: true };
          };

          const { activity } = openedFormRow;
          const isCompleted = !!openedFormRow.has_completed_object;
          const completedId = isCompleted
            ? openedFormRow.form_data?.id
            : undefined;

          const isNew = openedFormRow.id <= 0;
          const rowDate = toTimestamp(
            openedFormRow.baseTime,
            openedFormRow.start_time || '00:00',
          );
          const rowTimeTracks =
            isNew && openedFormRow.start_time && openedFormRow.finish_time
              ? [
                  {
                    start_time: toTimestamp(
                      openedFormRow.baseTime,
                      openedFormRow.start_time,
                    ),
                    finish_time: toTimestamp(
                      openedFormRow.baseTime,
                      openedFormRow.finish_time,
                    ),
                  },
                ]
              : [];
          const baseInitialData = isNew
            ? { boat_id: openedFormRow.boat_id, time_tracks: rowTimeTracks }
            : null;

          if (activity === 'SEEDING') {
            return (
              <SeedingModal
                visible={true}
                lineData={line}
                data={
                  openedFormRow.form_data ??
                  (isNew
                    ? {
                        ...baseInitialData,
                        planned_date_seed: rowDate,
                        planned_date_harvest: rowDate,
                      }
                    : null)
                }
                onCancel={handleCancel}
                onConfirm={handleCancel}
                updateID={completedId}
                overConfirm={isCompleted ? undefined : overConfirmDefault}
              />
            );
          }
          if (activity === 'ASSESSMENT') {
            return (
              <AssessmentModal
                visible={true}
                lineData={line}
                data={
                  openedFormRow.form_data ??
                  (isNew
                    ? { ...baseInitialData, assessment_date: rowDate }
                    : null)
                }
                onCancel={handleCancel}
                onConfirm={handleCancel}
                updateID={completedId}
                overConfirm={isCompleted ? undefined : overConfirmDefault}
              />
            );
          }
          if (activity === 'MAINTENANCE') {
            return (
              <MaintenanceModal
                visible={true}
                title={translate(lang, 'Maintenance Form')}
                lineData={line}
                data={
                  openedFormRow.form_data ??
                  (isNew
                    ? { ...baseInitialData, maintain_date: rowDate }
                    : null)
                }
                onCancel={handleCancel}
                onConfirm={handleCancel}
                updateId={completedId}
                overConfirm={isCompleted ? undefined : overConfirmDefault}
              />
            );
          }
          if (activity === 'INVENTORY_SEED') {
            return (
              <FloatManageModal
                visible={true}
                title='Inventory Seed Form'
                lineData={line}
                data={
                  openedFormRow.form_data ??
                  (isNew ? { ...baseInitialData, manage_date: rowDate } : null)
                }
                onCancel={handleCancel}
                onConfirm={handleCancel}
                updateId={completedId}
                overConfirm={isCompleted ? undefined : overConfirmDefault}
              />
            );
          }
          if (activity === 'HARVEST') {
            const isWaste = openedFormRow.activityLabel
              .toLowerCase()
              .includes('waste');
            return isWaste ? (
              <WasteHarvestModal
                visible={true}
                lineData={line}
                data={
                  openedFormRow.form_data ??
                  (isNew
                    ? { ...baseInitialData, complete_date: rowDate }
                    : null)
                }
                onCancel={handleCancel}
                onConfirm={handleCancel}
                updateID={completedId}
                overConfirm={isCompleted ? undefined : overConfirmHarvest}
              />
            ) : (
              <HarvestCompleteModal
                visible={true}
                lineData={line}
                data={
                  openedFormRow.form_data ??
                  (isNew
                    ? { ...baseInitialData, complete_date: rowDate }
                    : null)
                }
                onCancel={handleCancel}
                onConfirm={handleCancel}
                updateID={completedId}
                overConfirm={isCompleted ? undefined : overConfirmHarvest}
              />
            );
          }
          return null;
        })()}
      {taggingLine && (
        <LineTagModal
          visible={true}
          onClose={() => setTaggingLine(undefined)}
          lineData={taggingLine}
        />
      )}
    </div>
  );
};

export default TaskTracksPage;
